import React, { useMemo } from 'react';
import { Upload as AntdUpload, Button, List } from 'antd';
import PropTypes from 'prop-types';
import { UploadOutlined, DeleteFilled } from '@ant-design/icons';
import uploadImage from 'assets/img/upload.png';
import { formatBytes } from 'util/file';
import { MIME_TYPES } from 'util/fileUtil';
import Image from 'components/Antd/Image';
import T from 'i18n-react';
import classes from './Style.module.scss';
import './FileIcon.scss';
import cn from 'classnames';

const { Dragger } = AntdUpload;

const ItemRender = (node, file, list, actions, options) => {
  const { remove } = actions;

  const isImage = useMemo(() => {
    return Object.keys(MIME_TYPES.images)
      .reduce((acc, key) => {
        if (Array.isArray(MIME_TYPES.images[key])) {
          return [...acc, ...MIME_TYPES.images[key]];
        }
        return [...acc, MIME_TYPES.images[key]];
      }, [])
      .includes(file.type || file.mineType);
  }, [file]);

  const extension = useMemo(() => {
    const fileType = file.type || file.mineType;
    if (isImage) {
      return fileType.split('/')[1];
    }
    let ext = '';
    Object.keys(MIME_TYPES.documents).forEach((key) => {
      if (Array.isArray(MIME_TYPES.documents[key])) {
        if (MIME_TYPES.documents[key].includes(fileType)) {
          ext = key;
        }
      } else if (MIME_TYPES.documents[key] === fileType) {
        ext = key;
      }
    });

    return ext;
  }, [file, isImage]);

  const url = useMemo(() => {
    if (!isImage) return 'null';
    if (file.originFileObj) {
      return URL.createObjectURL(file.originFileObj);
    }
    return file.url || 'error';
  }, [file, isImage]);
  const shouldRemove = useMemo(() => {
    if (!options) return false;
    return options.shouldRemove;
  }, [options]);
  return (
    <List.Item
      className={classes.fileItem}
      extra={
        shouldRemove && <Button type="text" danger icon={<DeleteFilled onClick={remove} />} />
      }>
      <a target="_blank" rel="noopener noreferrer" href={url}>
        <List.Item.Meta
          avatar={
            isImage ? (
              <Image width={48} height={64} preview={false} src={url} />
            ) : (
              <div className="file-icon file-icon-md" data-type={extension} />
            )
          }
          title={file.name || file.fileName}
          description={formatBytes(file.size || file.fileSize)}
        />
      </a>
    </List.Item>
  );
};

export default function Upload({
  value,
  accept,
  onChange,
  dragger,
  disabled,
  multiple,
  supportText,
  shouldRemove,
  readonly = false,
  uploadButtonText = '',
  uploadButtonProps = {},
}) {
  const handleChange = ({ fileList }) => {
    if (multiple) {
      onChange(fileList);
    } else {
      let newFileList = [...fileList];

      // Limit to one file
      newFileList = newFileList.slice(-1);
      onChange(newFileList);
    }
  };

  if (dragger) {
    return (
      <Dragger
        multiple={multiple}
        accept={accept}
        beforeUpload={() => {
          return false;
        }}
        fileList={value}
        className={classes.upload}
        onChange={handleChange}
        // customRequest={customRequest}
        itemRender={(...args) => ItemRender(...args, { shouldRemove })}>
        <div className={classes.description}>
          <img src={uploadImage} alt="upload" />
          <p
            className={classes.title}
            dangerouslySetInnerHTML={{
              __html: T.translate('general.dropYourFilesHere', {}),
            }}
          />
          <span className={classes.caption}>{supportText}</span>
        </div>
      </Dragger>
    );
  }
  return (
    <AntdUpload
      multiple={multiple}
      accept={accept}
      beforeUpload={() => false}
      fileList={value}
      itemRender={(...args) => ItemRender(...args, { shouldRemove })}
      className={cn(classes.upload)}
      onChange={handleChange}
      disabled={disabled}>
      {!readonly && (
        <Button type="primary" ghost icon={<UploadOutlined />} {...uploadButtonProps}>
          {uploadButtonText}
        </Button>
      )}
    </AntdUpload>
  );
}

Upload.propTypes = {
  shouldRemove: PropTypes.bool,
  value: PropTypes.arrayOf(PropTypes.any),
  accept: PropTypes.arrayOf(PropTypes.string),
  dragger: PropTypes.bool,
  disabled: PropTypes.bool,
  uploadButtonText: PropTypes.string,
  supportText: PropTypes.string,
  onChange: PropTypes.func,
  multiple: PropTypes.bool,
  uploadButtonProps: PropTypes.shape({}),
};

Upload.defaultProps = {
  value: [],
  multiple: true,
  dragger: false,
  disabled: false,
  uploadButtonText: 'Upload',
  accept: [],
  supportText: 'Support: JPG, JPEG, PNG, PDF, DOC, XLSL',
  onChange: () => {},
  uploadButtonProps: {},
  shouldRemove: true,
};
